import { MessageInput } from '@chatscope/chat-ui-kit-react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import KeyboardVoiceOutlinedIcon from '@mui/icons-material/KeyboardVoiceOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import { type ChangeEvent, type ClipboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { VoiceRecorder } from './useVoiceRecorder';
import { PreviewBar, RecordingBar } from './voice-composer';

const chatIconButtonSx: SxProps<Theme> = {
  height: 40,
  width: 40,
  flexShrink: 0,
  p: 0,
  color: 'text.secondary',
  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
  '& svg': { fontSize: 22 },
};

interface ChatComposerProps {
  voice: VoiceRecorder;
  onSendVoice: () => void;
  fileInputRef: { current: HTMLInputElement | null };
  handleFileTypeField: (event: ChangeEvent<HTMLInputElement>) => void;
  setMgInputValue: (value: string) => void;
  handlePaste: (event: ClipboardEvent<HTMLDivElement>) => void;
  msgInputValue: string;
  handleSend: () => void;
  as?: 'MessageInput';
}

export function ChatComposer({
  voice,
  onSendVoice,
  fileInputRef,
  handleFileTypeField,
  setMgInputValue,
  handlePaste,
  msgInputValue,
  handleSend,
}: ChatComposerProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 2,
        boxSizing: 'border-box',
        display: 'flex',
        width: '100%',
        minHeight: 56,
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 0.25,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 1,
      }}
      onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {voice.status === 'recording' ? (
        <RecordingBar recorder={voice} />
      ) : voice.status === 'preview' && voice.preview ? (
        <PreviewBar
          preview={voice.preview}
          onDiscard={voice.discard}
          onSend={onSendVoice}
        />
      ) : (
        <>
          <IconButton
            sx={chatIconButtonSx}
            aria-label={t('chat.attachFile')}
            size="small"
            onClick={() => fileInputRef.current?.click()}
          >
            <AttachFileIcon />
          </IconButton>
          <Box
            component="input"
            ref={(node: HTMLInputElement | null) => {
              fileInputRef.current = node;
            }}
            type="file"
            sx={{ display: 'none' }}
            onChange={handleFileTypeField}
          />
          <MessageInput
            onChange={(msg) => setMgInputValue(msg)}
            onPaste={handlePaste}
            value={msgInputValue}
            attachButton={false}
            onSend={handleSend}
            placeholder={t('chat.placeholder')}
          />
          <IconButton
            sx={chatIconButtonSx}
            aria-label={t('chat.record')}
            size="small"
            title={t('chat.record')}
            disabled={voice.status === 'starting'}
            onClick={voice.start}
          >
            <KeyboardVoiceOutlinedIcon />
          </IconButton>
        </>
      )}
    </Box>
  );
}
